// #POW-100
import * as React from 'react';
import { useState, useContext, useEffect, useRef, useMemo } from 'react';
import { DataGrid, GridColDef, GridRowsProp, GridValidRowModel, GridFilterModel, GridToolbarContainer, GridToolbarFilterButton, getGridStringOperators, GridSortModel, getGridDateOperators } from '@mui/x-data-grid';
import useCustomFetch from '../hooks/useCustomFetch';
import AuthContext from '../context/AuthContext';
import Config from '../Config';
import { Box, Button, Chip, CircularProgress } from '@mui/material';
import { FiDownload, FiEye } from 'react-icons/fi';
import { ShippingData, Transaction } from '../types/Types'; // #POW-8
import { useTranslation } from "react-i18next"; // #POW-29

import type { ChipProps } from '@mui/material'; // add this import at the top if not present

function renderStatus(value: string, t: any) {
  let color: ChipProps['color'] = 'default';
  let label = value;
  switch (value) {
    case "created":
      color = 'primary';
      label = t('exportArchive.table.created');
      break;
    case "in_progress":
      color = 'secondary';
      label = t('exportArchive.table.inProgress');
      break;
    case "failed":
      color = 'error';
      label = t('exportArchive.table.failed');
      break;
    case "completed":
      color = 'success';
      label = t('exportArchive.table.completed');
      break;
    default:
      color = 'default'
}

  return <Chip label={label} color={color} size="small" />;
}

function renderTypeName(value: string, t: any) {
  if(!value)
    return "-";

  if(value === "export-excel-and-pdf-archive")
    return t('exportArchive.table.exportExcelAndPdfArchive');
}

function renderDownloadButton(filePath: string){
  if (filePath) {
    const fileName = filePath ? filePath.split('/').pop() || "" : "";
    return (
      <Button
        variant="text"
        onClick={() => {
          const link = document.createElement("a");
          link.href = filePath;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }}
      >
        <FiDownload />
      </Button>
    );
  }
  
  return "";

}
/**
 * #POW-11
 * Custom toolbar definition
 * @returns 
 */
function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarFilterButton />
    </GridToolbarContainer>
  );
}

export default function ExportArchiveDataGrid() {
  const { t } = useTranslation();  // #POW-29

  const customFetch = useCustomFetch()
  const { isReady } = useContext(AuthContext)
  const [isLoading, setIsLoading] = useState(false)

  /**
   * ---- Datasource, Rows and Columns definition 
   */
  const [rows, setRows] = useState<GridRowsProp>([])
  const [rowCount, setRowCount] = useState(0);

  // let's keep on backend supported filters #POW-11
  const supportedFilters = getGridStringOperators().filter((operator) => operator.value === 'contains');

  const supportedDateFilters = getGridDateOperators().filter((operator) => (operator.value === 'is')); 
  
  // pagination #POW-115
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'created_at',
      sort: 'desc',
    },
  ]);

  // filters #POW-115
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  /**
   * #POW-115
   * Enter Key press on filter input
   * @param event 
   */
  const handleFilterKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const newFilterModel = { ...filterModel };
      setFilterModel(newFilterModel);
      fetchData()
    }
  };

  /**
   * #POW-115
   * Handles filter model changes, reset included
   * @param model 
   */
  const setFilterModelChange =  (model: GridFilterModel) => {
    setFilterModel(model)
    if (model.items.length === 0) {
      setPaginationModel({page:0, pageSize:paginationModel.pageSize})
      fetchData(true)
    }

  }

  const columns: GridColDef[] = [
    { field: 'created_at', headerName: t('exportArchive.table.createdAt'), minWidth: 170,
      type: 'dateTime',
      valueGetter: (value) => value && new Date(value),
      filterOperators: supportedDateFilters 
    },
    {
      field: 'name', headerName: t('exportArchive.table.name'), minWidth: 230,
      filterable: false, // #POW-115
      renderCell: (params) => renderTypeName(params.value as any, t), 
    },
    {
      field: 'numberOfTransactions', headerName: t('exportArchive.table.numberOfTransactions'), minWidth: 140,
      filterable: false, // #POW-115
      sortable: false, // #POW-115
    },
    {
      field: 'status', headerName: t('exportArchive.table.status'), minWidth: 110,
      filterable: false, // #POW-115
      renderCell: (params) => renderStatus(params.value as any, t), 
    },
    { field: 'updated_at', headerName: t('exportArchive.table.updatedAt'), minWidth: 170,
      type: 'dateTime',
      valueGetter: (value) => value && new Date(value), 
      filterOperators: supportedDateFilters
    },
    {
      field: 'fileName', headerName: t('exportArchive.table.fileName'), minWidth: 300,
      filterOperators: supportedFilters
    },
    {
      field: 'actions', headerName: '', width: 100, filterable: false, sortable: false, renderCell: (params) => renderDownloadButton(params.row.filePath as string)
    },
  ];

 
  const fetchData = async (skipFilters=false) => {
    setIsLoading(true)
    
    var url: URL = new URL(`${Config.serverUrl}/api/logistic/transactions/get-exports`);
    
    // #POW-115 ALWAYS present parametes
    url.searchParams.append('page', '' + paginationModel.page)
    url.searchParams.append('page_size', '' + paginationModel.pageSize)

    // #POW-115 Sorting
    if (sortModel.length > 0) {
      url.searchParams.append('sort_field', ''+sortModel[0].field)
      url.searchParams.append('sort_direction', ''+sortModel[0].sort)
    }

    // #POW-115 Filters
    if (!skipFilters) {
      const filterParams = filterModel.items.reduce((acc: any, filter: any) => {
        acc[filter.field] = filter.value;
        return acc;
      }, {});
      Object.keys(filterParams).forEach((key) => {
        url.searchParams.append(key, filterParams[key]);
      });
    }
    const savedFilters = { ...filterModel };

    const response = await customFetch(url.toString(),
      {
        method: "GET",
        headers: {},
      },
    )
    const json = await response.json()
    setRows([])
    setRowCount(0)
    let countRows = 0;
    if (json && json.exports) {
      setRows(
        json.exports.map((item: any) => ({
          id: item.id,
          created_at: item.created_at,
          updated_at: item.updated_at,
          name: item.name,
          status: item.status,
          filePath: item.gc_bucket_path,
          fileName: item.gc_bucket_path ? item.gc_bucket_path.split('/').pop() : '-',
          numberOfTransactions: item.payload.transactions ? item.payload.transactions.length : ""
        }))
      )
      countRows = json.exports.length;
    }
    setRowCount(json.totalCount) // #POW-115

    if (!skipFilters) { // #POW-115
      setFilterModel(savedFilters);
    }

    setIsLoading(false)
  }


  React.useEffect(() => {
    if (isReady) {
      fetchData()
    }
  }, [isReady, paginationModel, sortModel]); // #POW-115

  return (
    <>
      <Box sx={{ width: '100%', maxWidth: { sm: '100%' }, height: '100%' }}>
        <DataGrid
          autoHeight
          disableRowSelectionOnClick
          loading={isLoading}
          //checkboxSelection
          rows={rows}
          rowCount={rowCount}
          columns={columns}
          density="compact"

          // ------------ #POW-115 filters ------------
          filterMode="server"
          onFilterModelChange={setFilterModelChange}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }

          // ------------ #POW-115 pagination & sorting ------------ 
          paginationMode="server"
          initialState={{
            filter: { filterModel: filterModel }
          }}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}

          sortingOrder={['desc', 'asc']}
          sortModel={sortModel}
          onSortModelChange={setSortModel}

          slots={{ toolbar: CustomToolbar }}
          slotProps={{
            filterPanel: {
              filterFormProps: {
                logicOperatorInputProps: {
                  variant: 'outlined',
                  size: 'small',
                },
                columnInputProps: {
                  variant: 'outlined',
                  size: 'small',
                  sx: { mt: 'auto' },
                },
                operatorInputProps: {
                  variant: 'outlined',
                  size: 'small',
                  sx: { mt: 'auto' },
                },
                valueInputProps: {
                  InputComponentProps: {
                    variant: 'outlined',
                    size: 'small',
                    onKeyPress: handleFilterKeyPress,
                  },
                },
              },
            },
          }}
          getRowId={(row: GridValidRowModel) => row.id}
        />
      </Box>
    </>
  );
}
